import { Editor, Transforms, Path, Node, type NodeEntry } from 'slate';
import {
  getCurrentListItems,
  getPreviousSiblingListItem,
  getParentList,
} from './utils';
import { isList, isListItem } from './guards';
import type { ListItemElement } from '../../types/aha-editor';

const MAX_DEPTH = 15;

class ListDepthUtils {
  static getCurrentItemDepth(
    editor: Editor,
    currentItemEntry: NodeEntry<ListItemElement>
  ) {
    const currentItemPath = currentItemEntry[1];
    const listAncestors = Editor.nodes(editor, {
      at: currentItemPath,
      reverse: true,
      match: (element, path) =>
        isList(element) && Path.isAncestor(path, currentItemPath),
    });

    return Array.from(listAncestors).length;
  }

  static increaseItemDepth(
    editor: Editor,
    currentItemEntry: NodeEntry<ListItemElement>
  ) {
    const [currentItem, currentPath] = currentItemEntry;
    const currentPathRef = Editor.pathRef(editor, currentPath);
    if (!currentItem) return;

    const currentDepth = this.getCurrentItemDepth(editor, currentItemEntry);
    if (currentDepth >= MAX_DEPTH) return;

    const parentListEntry = getParentList(editor, currentPath);
    if (!parentListEntry) return;

    const [list, parentListPath] = parentListEntry;

    const previousItemEntry = getPreviousSiblingListItem(editor, currentPath);
    Editor.withoutNormalizing(editor, () => {
      if (previousItemEntry) {
        const [previousItem, previousPath] = previousItemEntry;
        const previousItemLastChild =
          previousItem.children[previousItem.children.length - 1];

        if (isList(previousItemLastChild)) {
          // If the last child of the previous element is a list, then it just moves the current element to the end of the existing list.
          Transforms.moveNodes(editor, {
            at: currentPath,
            to: [
              ...previousPath,
              previousItem.children.length - 1,
              previousItemLastChild.children.length,
            ],
          });
          // Otherwise, it proceeds to create a new list at the destination and moves the node into it.
        } else {
          Transforms.insertNodes(
            editor,
            { type: list.type, children: [] },
            {
              at: [...previousPath, previousItem.children.length],
            }
          );
          Transforms.moveNodes(editor, {
            at: currentPathRef.current!,
            to: [...previousPath, previousItem.children.length, 0],
          });
        }
      } else {
        Transforms.insertNodes(
          editor,
          {
            type: 'list_item',
            children: [
              {
                type: list.type,
                children: [],
              },
            ],
          },
          { at: [...parentListPath, 0] }
        );
        Transforms.moveNodes(editor, {
          at: currentPathRef.current!,
          to: [...parentListPath, 0, 0, 0],
        });
      }
    });

    currentPathRef.unref();
  }

  static increaseDepth(editor: Editor) {
    const listItems = getCurrentListItems(editor);
    const pathRefs = listItems.map(([, path]) => Editor.pathRef(editor, path));

    for (const pathRef of pathRefs) {
      const path = pathRef.unref();
      if (!path) continue;

      const node = Node.get(editor, path) as ListItemElement;
      ListDepthUtils.increaseItemDepth(editor, [node, path]);
    }
  }

  static decreaseItemDepth(
    editor: Editor,
    currentItemEntry: NodeEntry<ListItemElement>,
    isBackspace = false
  ) {
    const [currentItem, currentItemPath] = currentItemEntry;
    if (!currentItem) return;

    const currentListEntry = getParentList(editor, currentItemPath);
    if (!currentListEntry) return;
    const [currentList, currentListPath] = currentListEntry;

    // Get current list's parent
    const [parentListItem, parentListItemPath] = Editor.parent(
      editor,
      currentListPath
    );

    // Get parent list.
    const parentListEntry = getParentList(editor, currentListPath);
    const parentListPath = parentListEntry ? parentListEntry[1] : undefined;
    const currentItemDepth = this.getCurrentItemDepth(editor, currentItemEntry);

    // If pressing backspace would remove this item from the list, keep its children
    // at their current depth.
    if (isBackspace && currentItemDepth === 1) {
      let childIndex = 0;
      for (const child of currentItem.children) {
        if (isList(child)) {
          const pathRefs = child.children
            .map((item, index) => {
              if (!isListItem(item)) return null;
              return Editor.pathRef(editor, [
                ...currentItemPath,
                childIndex,
                index,
              ]);
            })
            .filter(Boolean);

          for (const pathRef of pathRefs) {
            const path = pathRef.unref();
            if (!path) continue;

            const node = Node.get(editor, path) as ListItemElement;
            ListDepthUtils.increaseItemDepth(editor, [node, path]);
          }
        }
        childIndex++;
      }
    }

    const currentIndex = currentItemPath[currentItemPath.length - 1];
    const previousSiblings = currentList.children.slice(0, currentIndex);
    const nextSiblings = currentList.children.slice(
      currentIndex + 1
    ) as ListItemElement[];

    // Move next siblings as children of the current item.
    for (const sibling of nextSiblings) {
      const nextSiblingPath = [...currentListPath, currentIndex + 1];
      ListDepthUtils.increaseItemDepth(editor, [sibling, nextSiblingPath]);
    }

    Editor.withoutNormalizing(editor, () => {
      if (typeof parentListPath !== 'undefined') {
        const shouldRemoveParentListItem = parentListItem.children.length === 1;

        const newPath = Path.next(parentListItemPath);
        Transforms.moveNodes(editor, {
          at: currentItemPath,
          to: newPath,
        });

        if (previousSiblings.length === 0) {
          Transforms.removeNodes(editor, { at: currentListPath });
          if (shouldRemoveParentListItem) {
            Transforms.removeNodes(editor, { at: parentListItemPath });
          }
        }
      } else {
        Transforms.moveNodes(editor, {
          at: currentItemPath,
          to: Path.next(currentListPath),
        });
        Transforms.unwrapNodes(editor, { at: Path.next(currentListPath) });

        if (previousSiblings.length === 0) {
          Transforms.removeNodes(editor, { at: currentListPath });
        }
      }
    });
  }

  static decreaseDepth(editor: Editor) {
    const listItems = getCurrentListItems(editor);
    const pathRefs = listItems.map(([, path]) => Editor.pathRef(editor, path));

    for (const pathRef of pathRefs) {
      const path = pathRef.unref();
      if (!path) continue;

      const node = Node.get(editor, path) as ListItemElement;
      ListDepthUtils.decreaseItemDepth(editor, [node, path]);
    }
  }
}

export default ListDepthUtils;
